import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Palette, Loader2, Save, LayoutTemplate, Sidebar, Layers, Table } from 'lucide-react'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { themeService, Theme } from '../services/theme.service'

const THEME_PRESETS = {
    antiguo: {
        "background": "#ffffff",
        "foreground": "#0f172a",
        "muted": "#f1f5f9",
        "muted-foreground": "#64748b",
        "border": "#e2e8f0",

        "primary": "#3B7D3B",
        "primary-foreground": "#ffffff",
        "secondary": "#f8fafc",
        "secondary-foreground": "#0f172a",
        "accent": "#f1f5f9",
        "accent-foreground": "#0f172a",
        "destructive": "#ef4444",
        "destructive-foreground": "#ffffff",

        "card": "#ffffff",
        "card-foreground": "#0f172a",
        "card-description": "#64748b",
        "card-border": "#e2e8f0",
        "card-header": "#f8fafc",
        "popover": "#ffffff",
        "popover-foreground": "#0f172a",
        "popover-border": "#e2e8f0",
        "dropdown-item-hover": "#f1f5f9",
        "dropdown-item-hover-text": "#0f172a",
        "dialog-overlay": "rgba(0,0,0,0.5)",

        "sidebar": "#ffffff",
        "sidebar-foreground": "#0f172a",
        "sidebar-primary": "#3B7D3B",
        "sidebar-primary-foreground": "#ffffff",
        "sidebar-accent": "#f1f5f9",
        "sidebar-accent-foreground": "#0f172a",
        "sidebar-border": "#e2e8f0",
        "sidebar-branch": "#ffffff",
        "sidebar-branch-hover": "#f1f5f9",

        "table-bg": "#ffffff",
        "table-foreground": "#0f172a",
        "table-header-bg": "#f8fafc",
        "table-header-foreground": "#64748b",
        "table-row-hover": "#f1f5f9",
    },
    nuevo: {
        "background": "#BCE9FD",
        "foreground": "#1C2B41",
        "muted": "#96D9F9",
        "muted-foreground": "#475569",
        "border": "#96D9F9",

        "primary": "#3B82F6",
        "primary-foreground": "#ffffff",
        "secondary": "#263C61",
        "secondary-foreground": "#ffffff",
        "accent": "#263C61",
        "accent-foreground": "#ffffff",
        "destructive": "#B24C4C",
        "destructive-foreground": "#ffffff",

        "card": "#1C2E4A",
        "card-foreground": "#ffffff",
        "card-description": "#94A3B8",
        "card-border": "#1C2E4A",
        "card-header": "#1C2E4A",
        "popover": "#1C2E4A",
        "popover-foreground": "#ffffff",
        "popover-border": "#263C61",
        "dropdown-item-hover": "#263C61",
        "dropdown-item-hover-text": "#ffffff",
        "dialog-overlay": "rgba(0,0,0,0.5)",

        "sidebar": "#172640",
        "sidebar-foreground": "#CBD5E1",
        "sidebar-primary": "#3B82F6",
        "sidebar-primary-foreground": "#ffffff",
        "sidebar-accent": "#263C61",
        "sidebar-accent-foreground": "#ffffff",
        "sidebar-border": "#172640",
        "sidebar-branch": "#1C2E4A",
        "sidebar-branch-hover": "#263C61",

        "table-bg": "#1C2E4A",
        "table-foreground": "#ffffff",
        "table-header-bg": "#1C2E4A",
        "table-header-foreground": "#94A3B8",
        "table-row-hover": "#263C61",
    },
    halloween: {
        "background": "#09090b",
        "foreground": "#fafafa",
        "muted": "#27272a",
        "muted-foreground": "#a1a1aa",
        "border": "#ea580c",

        "primary": "#ea580c",
        "primary-foreground": "#fafafa",
        "secondary": "#27272a",
        "secondary-foreground": "#fafafa",
        "accent": "#27272a",
        "accent-foreground": "#fafafa",
        "destructive": "#991b1b",
        "destructive-foreground": "#fafafa",

        "card": "#18181b",
        "card-foreground": "#fafafa",
        "card-description": "#a1a1aa",
        "card-border": "#ea580c",
        "card-header": "#18181b",
        "popover": "#18181b",
        "popover-foreground": "#fafafa",
        "popover-border": "#ea580c",
        "dropdown-item-hover": "#27272a",
        "dropdown-item-hover-text": "#fafafa",
        "dialog-overlay": "rgba(0,0,0,0.8)",

        "sidebar": "#09090b",
        "sidebar-foreground": "#a1a1aa",
        "sidebar-primary": "#ea580c",
        "sidebar-primary-foreground": "#fafafa",
        "sidebar-accent": "#27272a",
        "sidebar-accent-foreground": "#fafafa",
        "sidebar-border": "#ea580c",
        "sidebar-branch": "#18181b",
        "sidebar-branch-hover": "#27272a",

        "table-bg": "#18181b",
        "table-foreground": "#fafafa",
        "table-header-bg": "#27272a",
        "table-header-foreground": "#a1a1aa",
        "table-row-hover": "#27272a",
    }
}

const THEME_CATEGORIES = [
    {
        id: 'layout',
        title: 'Layout (Base)',
        icon: LayoutTemplate,
        description: 'Colores principales para la estructura, fondos y textos de la aplicación.',
        properties: [
            { key: 'background', label: 'Fondo de Pantalla', default: '#ffffff' },
            { key: 'foreground', label: 'Color de Texto Principal', default: '#2c3e50' },
            { key: 'muted', label: 'Fondo Secundario (Muted)', default: '#ececf0' },
            { key: 'muted-foreground', label: 'Texto Secundario (Muted)', default: '#717182' },
            { key: 'border', label: 'Color de Bordes', default: '#e2e8f0' },
        ]
    },
    {
        id: 'identidad',
        title: 'Identidad Corporativa',
        icon: Palette,
        description: 'Colores principales de la empresa.',
        properties: [
            { key: 'primary', label: 'Color Primario', default: '#3B7D3B' },
            { key: 'primary-foreground', label: 'Texto en Primario', default: '#ffffff' },
            { key: 'secondary', label: 'Color Secundario', default: '#e8f5e9' },
            { key: 'secondary-foreground', label: 'Texto en Secundario', default: '#2c3e50' },
            { key: 'accent', label: 'Color Acento', default: '#e1f5fe' },
            { key: 'accent-foreground', label: 'Texto en Acento', default: '#2c3e50' },
            { key: 'destructive', label: 'Color de Peligro', default: '#d4183d' },
            { key: 'destructive-foreground', label: 'Texto en Peligro', default: '#ffffff' },
        ]
    },
    {
        id: 'tarjetas',
        title: 'Tarjetas y Flotantes',
        icon: Layers,
        description: 'Colores de las tarjetas, paneles, menús desplegables, popovers y diálogos modales.',
        properties: [
            { key: 'card', label: 'Fondo de Tarjetas', default: '#ffffff' },
            { key: 'card-foreground', label: 'Texto de Tarjetas', default: '#2c3e50' },
            { key: 'card-description', label: 'Subtítulo / Descripción de Tarjeta', default: '#717182' },
            { key: 'card-border', label: 'Borde de Tarjetas', default: 'rgba(0,0,0,0.1)' },
            { key: 'card-header', label: 'Fondo del Encabezado de Tarjeta', default: '#f8fafc' },
            { key: 'popover', label: 'Fondo de Menús Flotantes / Dropdowns', default: '#ffffff' },
            { key: 'popover-foreground', label: 'Texto de Menús Flotantes', default: '#2c3e50' },
            { key: 'popover-border', label: 'Borde de Menús Flotantes', default: 'rgba(0,0,0,0.1)' },
            { key: 'dropdown-item-hover', label: 'Hover de Items en Dropdown', default: '#e8f5e9' },
            { key: 'dropdown-item-hover-text', label: 'Texto en Hover de Dropdown', default: '#2c3e50' },
            { key: 'dialog-overlay', label: 'Overlay del Diálogo Modal', default: 'rgba(0,0,0,0.5)' },
        ]
    },
    {
        id: 'sidebar',
        title: 'Sidebar (Navegación)',
        icon: Sidebar,
        description: 'Colores exclusivos para el menú lateral.',
        properties: [
            { key: 'sidebar', label: 'Fondo del Sidebar', default: '#ffffff' },
            { key: 'sidebar-foreground', label: 'Texto e Íconos', default: '#2c3e50' },
            { key: 'sidebar-primary', label: 'Menú Activo (Primario)', default: '#3B7D3B' },
            { key: 'sidebar-primary-foreground', label: 'Texto de Menú Activo', default: '#ffffff' },
            { key: 'sidebar-accent', label: 'Fondo al pasar el mouse (Hover)', default: '#e8f5e9' },
            { key: 'sidebar-accent-foreground', label: 'Texto en Hover', default: '#2c3e50' },
            { key: 'sidebar-border', label: 'Bordes del Sidebar', default: '#e2e8f0' },
            { key: 'sidebar-branch', label: 'Fondo Selector de Sucursales', default: '#ffffff' },
            { key: 'sidebar-branch-hover', label: 'Hover Selector de Sucursales', default: '#e8f5e9' },
        ]
    },
    {
        id: 'tablas',
        title: 'Tablas y Listados',
        icon: Table,
        description: 'Colores específicos para las tablas de datos y listas de registros.',
        properties: [
            { key: 'table-bg', label: 'Fondo de Tabla', default: '#ffffff' },
            { key: 'table-foreground', label: 'Texto de Tabla', default: '#2c3e50' },
            { key: 'table-header-bg', label: 'Fondo de Encabezado', default: '#f8fafc' },
            { key: 'table-header-foreground', label: 'Texto de Encabezado', default: '#717182' },
            { key: 'table-row-hover', label: 'Hover de Fila', default: '#f1f5f9' },
        ]
    }
]

export function Themes() {
    const queryClient = useQueryClient()

    // El estado local de las propiedades para reflejar los cambios en tiempo real
    const [properties, setProperties] = useState<Record<string, string>>({})

    const { data: theme, isLoading } = useQuery({
        queryKey: ['activeTheme'],
        queryFn: themeService.getCurrentTheme
    })

    useEffect(() => {
        if (theme && theme.properties) {
            const props: Record<string, string> = {}
            theme.properties.forEach(p => {
                props[p.key] = p.value.value as string
            })
            setProperties(props)
        }
    }, [theme])

    const updateMutation = useMutation({
        mutationFn: () => {
            const formattedProps: Record<string, { type: string, value: any }> = {}
            Object.entries(properties).forEach(([k, v]) => {
                formattedProps[k] = { type: 'color', value: v }
            })
            return themeService.updateCurrentTheme(formattedProps)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['activeTheme'] })
            toast.success('Configuración de tema guardada correctamente')
        },
        onError: () => toast.error('Error al guardar el tema')
    })

    const handleChange = (key: string, value: string) => {
        setProperties(prev => ({ ...prev, [key]: value }))
        // Inyectar al CSS :root en tiempo real
        document.documentElement.style.setProperty(`--${key}`, value)
    }

    const applyPreset = (presetKey: 'antiguo' | 'nuevo' | 'halloween') => {
        const preset = THEME_PRESETS[presetKey]
        setProperties(preset)
        Object.entries(preset).forEach(([key, value]) => {
            document.documentElement.style.setProperty(`--${key}`, value)
        })
        toast.success(`Plantilla cargada. Haz clic en Guardar para aplicar permanentemente.`)
    }

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
        )
    }

    return (
        <div className="min-h-full bg-background px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto flex max-w-4xl flex-col gap-6">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-2">
                        <h1 className="text-2xl font-semibold text-foreground">
                            Tema de la Compañía
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Personaliza la identidad visual de la plataforma. Los cambios se previsualizan instantáneamente.
                        </p>
                    </div>
                    <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Guardar Tema
                    </Button>
                </div>

                <div className="flex gap-4 p-4 rounded-lg bg-card border border-border">
                    <div className="flex flex-col gap-1 flex-1">
                        <span className="font-semibold text-card-foreground">Plantillas Prediseñadas</span>
                        <span className="text-sm text-card-description">Carga rápidamente una paleta de colores completa.</span>
                    </div>
                    <div className="flex gap-2 items-center">
                        <Button variant="outline" onClick={() => applyPreset('antiguo')}>
                            Diseño Antiguo
                        </Button>
                        <Button variant="default" onClick={() => applyPreset('nuevo')}>
                            Diseño Nuevo
                        </Button>
                        {/*<Button className="bg-[#ea580c] hover:bg-[#c2410c] text-white" onClick={() => applyPreset('halloween')}>
                            Halloween 
                        </Button> */}

                    </div>
                </div>

                <div className="grid gap-6">
                    {THEME_CATEGORIES.map((category) => (
                        <Card key={category.id} className="bg-card">
                            <CardHeader className="border-b border-border pb-4">
                                <CardTitle className="flex items-center gap-2 text-lg text-card-foreground">
                                    <category.icon className="h-5 w-5 text-primary" />
                                    {category.title}
                                </CardTitle>
                                <CardDescription className="text-card-description">
                                    {category.description}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
                                {category.properties.map((prop) => (
                                    <div key={prop.key} className="space-y-2">
                                        <label className="text-sm font-medium text-card-foreground">
                                            {prop.label}
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                type="color"
                                                value={properties[prop.key] || prop.default}
                                                onChange={(e) => handleChange(prop.key, e.target.value)}
                                                className="h-10 w-12 rounded-md border border-border bg-transparent p-1 cursor-pointer"
                                            />
                                            <Input
                                                value={properties[prop.key] || prop.default}
                                                onChange={(e) => handleChange(prop.key, e.target.value)}
                                                className="font-mono text-card-foreground border-border"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    )
}
